import { useEffect, useRef, useState } from 'react'
import { useUser } from '@/contexts/UserContext'
import { useToast } from '@/contexts/ToastContext'
import {
  createSocialPost,
  criarFeedback,
  getFeedbacksPublicos,
  getSocialPosts,
  uploadImage,
  type FeedbackPublico,
  type SocialPost,
} from '@/api/usuariosApi'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import { CONTEUDO_CHAVES } from '@/api/adminApi'
import { ClientPageLayout, clientPageStyles as layout } from '@/components/ClientPageLayout'
import { useRequireConteudoFlag } from '@/utils/featureFlags'

function EmptyState({ icon, message }: { icon: string; message: string }) {
  return (
    <div className={layout.state}>
      <span className={layout.stateIcon} aria-hidden>
        {icon}
      </span>
      <p>{message}</p>
    </div>
  )
}

export function SocialPage() {
  const { loading: featureLoading } = useRequireConteudoFlag(CONTEUDO_CHAVES.menu_show_social)
  const { user } = useUser()
  const { toast } = useToast()
  const [tab, setTab] = useState<'feed' | 'feedbacks'>('feed')

  const [posts, setPosts] = useState<SocialPost[]>([])
  const [feedbacks, setFeedbacks] = useState<FeedbackPublico[]>([])
  const [loading, setLoading] = useState(true)

  const [postTexto, setPostTexto] = useState('')
  const [postMediaUrl, setPostMediaUrl] = useState('')
  const [postMediaUploading, setPostMediaUploading] = useState(false)
  const [feedbackTexto, setFeedbackTexto] = useState('')
  const [saving, setSaving] = useState(false)
  const postFileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([getSocialPosts(), getFeedbacksPublicos()])
      .then(([p, f]) => {
        if (!cancelled) {
          setPosts(p)
          setFeedbacks(f)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPosts([])
          setFeedbacks([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const handleUploadPostImagem = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPostMediaUploading(true)
    try {
      const url = await uploadImage(file)
      setPostMediaUrl(url)
      toast('Imagem carregada para o post.', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao enviar imagem.', 'error')
    } finally {
      setPostMediaUploading(false)
      e.target.value = ''
    }
  }

  const handlePublicarPost = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user?.id) return
    if (!postTexto.trim() && !postMediaUrl.trim()) {
      toast('Escreva algo ou adicione mídia.', 'error')
      return
    }
    setSaving(true)
    try {
      const created = await createSocialPost({
        texto: postTexto.trim() || undefined,
        media_url: postMediaUrl.trim() || undefined,
      })
      setPosts((prev) => [{ ...created, usuario_nome: user.nome }, ...prev])
      setPostTexto('')
      setPostMediaUrl('')
      toast('Post publicado!', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao publicar post.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleEnviarFeedback = async (e: React.FormEvent) => {
    e.preventDefault()
    const nome = user?.nome || 'Cliente'
    if (!feedbackTexto.trim()) {
      toast('Escreva o seu feedback.', 'error')
      return
    }
    setSaving(true)
    try {
      await criarFeedback({
        nome,
        texto: feedbackTexto.trim(),
      })
      setFeedbackTexto('')
      toast('Feedback enviado! Assim que aprovado, aparecerá nesta aba.', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao enviar feedback.', 'error')
    } finally {
      setSaving(false)
    }
  }

  if (featureLoading) return null

  return (
    <ClientPageLayout>
      <div className={layout.tabs}>
        <button
          type="button"
          className={`${layout.tab} ${tab === 'feed' ? layout.tabActive : ''}`}
          onClick={() => setTab('feed')}
        >
          Feed
        </button>
        <button
          type="button"
          className={`${layout.tab} ${tab === 'feedbacks' ? layout.tabActive : ''}`}
          onClick={() => setTab('feedbacks')}
        >
          Testemunhos
        </button>
      </div>

      {tab === 'feed' && (
        <>
          <form className={layout.card} onSubmit={handlePublicarPost}>
            <h2 className={layout.sectionTitle}>Criar publicação</h2>
            <textarea
              className={layout.textarea}
              placeholder="Partilhe a sua experiência…"
              value={postTexto}
              onChange={(e) => setPostTexto(e.target.value)}
            />
            <input
              ref={postFileInputRef}
              type="file"
              accept="image/*"
              onChange={handleUploadPostImagem}
              style={{ display: 'none' }}
            />
            <div className={layout.btnRow}>
              <button
                type="button"
                className={layout.btnSecondary}
                onClick={() => postFileInputRef.current?.click()}
                disabled={postMediaUploading || saving}
              >
                {postMediaUploading
                  ? 'A enviar…'
                  : postMediaUrl
                    ? 'Trocar imagem'
                    : 'Adicionar imagem'}
              </button>
              {postMediaUrl && (
                <button
                  type="button"
                  className={layout.btnSecondary}
                  onClick={() => setPostMediaUrl('')}
                  disabled={postMediaUploading || saving}
                >
                  Remover
                </button>
              )}
            </div>
            {postMediaUrl && (
              <img
                src={resolveMediaUrl(postMediaUrl)}
                alt="Pré-visualização"
                className={layout.previewImg}
              />
            )}
            <button className={layout.btnPrimary} type="submit" disabled={saving || postMediaUploading}>
              {saving ? 'A publicar…' : 'Publicar'}
            </button>
          </form>

          {loading ? (
            <EmptyState icon="⏳" message="A carregar publicações…" />
          ) : posts.length === 0 ? (
            <EmptyState icon="💬" message="Ainda não há publicações. Seja o primeiro a partilhar!" />
          ) : (
            <div className={layout.feedList}>
              {posts.map((p) => (
                <article key={p.id} className={layout.feedCard}>
                  <h3 className={layout.feedAuthor}>
                    {p.usuario_nome || `Cliente #${p.usuario_id}`}
                  </h3>
                  {p.texto && <p className={layout.feedText}>{p.texto}</p>}
                  {p.media_url &&
                    (/\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(p.media_url) ? (
                      <video className={layout.feedMedia} src={p.media_url} controls />
                    ) : (
                      <img className={layout.feedMedia} src={p.media_url} alt="" />
                    ))}
                </article>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'feedbacks' && (
        <>
          <form className={layout.card} onSubmit={handleEnviarFeedback}>
            <h2 className={layout.sectionTitle}>Enviar testemunho</h2>
            <textarea
              className={layout.textarea}
              placeholder="Conte como foi a sua experiência…"
              value={feedbackTexto}
              onChange={(e) => setFeedbackTexto(e.target.value)}
            />
            <button className={layout.btnPrimary} type="submit" disabled={saving}>
              {saving ? 'A enviar…' : 'Enviar feedback'}
            </button>
          </form>

          {loading ? (
            <EmptyState icon="⏳" message="A carregar testemunhos…" />
          ) : feedbacks.length === 0 ? (
            <EmptyState icon="❤️" message="Ainda não há testemunhos aprovados." />
          ) : (
            <div className={layout.feedList}>
              {feedbacks.map((f) => (
                <article key={f.id} className={layout.feedCard}>
                  <h3 className={layout.feedAuthor}>{f.nome}</h3>
                  <p className={layout.feedText}>{f.texto}</p>
                </article>
              ))}
            </div>
          )}
        </>
      )}
    </ClientPageLayout>
  )
}
